import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import {
  CheckCircle,
  AlertCircle,
  Save,
  Plus,
  X,
  Image as ImageIcon,
  Upload,
  Camera,
  Sparkles,
  Loader2,
  User,
  MapPin,
  Briefcase,
  Building2,
  Phone,
  Mail,
  Tags,
  Layers,
  Lock,
} from "lucide-react";
import {
  getMarketplaceProfile,
  createMarketplaceProfile,
  updateMarketplaceProfile,
  MarketplaceProfile,
  ProfessionalType,
  ProfileType,
  PortfolioItem,
} from "../../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { showSuccessToast, showErrorToast } from "../../../atoms/Utlis/Toast";
import { resolveImage } from "../../../../Utils/helper";
import { MarketplaceLoadingSpinner } from "./MarketplaceEmptyState";
import {
  PageContainer,
  FormScrollContainer,
  PageTitle,
  FormHero,
  FormHeroGlow,
  FormHeroRow,
  FormHeroText,
  FormHeroBadge,
  FormHeroSubtitle,
  FormCard,
  SectionHeading,
  SectionIcon,
  SectionHeadingBody,
  SectionHeadingTitle,
  SectionHeadingSub,
  RowGrid,
  FormGroup,
  FormLabel,
  TextInput,
  TextAreaInput,
  SelectDropdown,
  FooterButtonsRow,
  SubmitHint,
  SaveButton,
  Req,
  ToggleWrapper,
  ToggleLabel,
  ToggleSwitch,
  gradient,
} from "../formSharedStyles";

const EndFooter = styled(FooterButtonsRow)`
  position: static;
  margin-top: 20px;
  margin-bottom: 8px;
`;

const ProfilePage = styled(PageContainer)`
  height: auto;
  min-height: 100%;
  overflow: visible;
  flex-shrink: 0;
`;

const ProfileScroll = styled(FormScrollContainer)`
  flex: none;
  overflow: visible;
  min-height: unset;
  padding-right: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import { PrimaryButton, SecondaryButton, ModalActions } from "../quotationMasterListShared";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled(Loader2)`
  animation: ${spin} 0.8s linear infinite;
`;

interface ProfileForm {
  display_name: string;
  bio: string;
  profile_picture: string;
  city: string;
  state: string;
  country: string;
  is_public: boolean;
  is_public_locked?: boolean;
  is_active?: boolean;
  work_categories: string;
  portfolio: PortfolioItem[];
  professional_type: ProfessionalType | "";
  profession: string;
  experience_years: string;
  skills: string;
  services_offered: string;
  team_size: string;
  business_info: string;
  contact_phone: string;
  contact_email: string;
}

const defaultForm: ProfileForm = {
  display_name: "",
  bio: "",
  profile_picture: "",
  city: "",
  state: "",
  country: "India",
  is_public: true,
  is_public_locked: false,
  is_active: true,
  work_categories: "",
  portfolio: [],
  professional_type: "",
  profession: "",
  experience_years: "",
  skills: "",
  services_offered: "",
  team_size: "",
  business_info: "",
  contact_phone: "",
  contact_email: "",
};

const MarketplaceProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<MarketplaceProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<ProfileForm>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [showPhotoSourceModal, setShowPhotoSourceModal] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [newPortTitle, setNewPortTitle] = useState("");
  const [newPortUrl, setNewPortUrl] = useState("");

  const profileType: ProfileType = profile?.profile_type ?? "studio";
  const isFreelancer = profileType === "freelancer";
  const isStudio = profileType === "studio";

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await getMarketplaceProfile();
      if (res?.data) {
        setProfile(res.data);
        const p = res.data;
        setForm({
          display_name: p.display_name ?? "",
          bio: p.bio ?? "",
          profile_picture: p.profile_picture ?? "",
          city: p.city ?? "",
          state: p.state ?? "",
          country: p.country ?? "India",
          is_public: p.is_public ?? true,
          is_public_locked: Boolean(p.is_public_locked),
          is_active: p.is_active ?? true,
          work_categories: (p.work_categories ?? []).join(", "),
          portfolio: p.portfolio ?? [],
          professional_type: p.professional_type ?? "",
          profession: p.profession ?? "",
          experience_years: p.experience_years != null ? String(p.experience_years) : "",
          skills: (p.skills ?? []).join(", "),
          services_offered: (p.services_offered ?? []).join(", "),
          team_size: p.team_size != null ? String(p.team_size) : "",
          business_info: p.business_info ?? "",
          contact_phone: p.contact_phone ?? "",
          contact_email: p.contact_email ?? "",
        });
      }
    } catch {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 400, height: 400, facingMode: "user" },
      });
      setCameraStream(stream);
      setShowCamera(true);
      setShowPhotoSourceModal(false);
    } catch {
      showErrorToast("Could not access camera");
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const capturePhoto = () => {
    const video = document.getElementById("webcam-video") as HTMLVideoElement;
    const canvas = document.createElement("canvas");
    if (video) {
      canvas.width = video.videoWidth || 400;
      canvas.height = video.videoHeight || 400;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const file = new File([blob], "profile_photo.jpg", { type: "image/jpeg" });
              setImageFile(file);
              showSuccessToast("Photo captured!");
            }
            stopCamera();
          },
          "image/jpeg",
          0.9
        );
      }
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    showSuccessToast("Photo selected!");
    setShowPhotoSourceModal(false);
  };

  const addPortfolioItem = () => {
    if (!newPortTitle.trim() || !newPortUrl.trim()) return;
    set("portfolio", [...form.portfolio, { title: newPortTitle.trim(), url: newPortUrl.trim() }]);
    setNewPortTitle("");
    setNewPortUrl("");
  };

  const removePortfolioItem = (idx: number) =>
    set(
      "portfolio",
      form.portfolio.filter((_, i) => i !== idx)
    );

  const csvToArray = (val: string) =>
    val
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.display_name.trim()) {
      showErrorToast("Display name is required");
      return;
    }
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("display_name", form.display_name.trim());
      formData.append("bio", form.bio.trim());
      formData.append("city", form.city.trim());
      formData.append("state", form.state.trim());
      formData.append("country", form.country.trim());
      formData.append(
        "is_public",
        String(form.is_public_locked || form.is_active === false ? false : form.is_public)
      );
      formData.append("contact_phone", form.contact_phone.trim());
      formData.append("contact_email", form.contact_email.trim());
      formData.append("profile_type", profileType);
      csvToArray(form.work_categories).forEach((cat) => {
        formData.append("work_categories", cat);
      });
      formData.append("portfolio", JSON.stringify(form.portfolio));

      if (isFreelancer) {
        if (form.professional_type) formData.append("professional_type", form.professional_type);
        formData.append("profession", form.profession.trim());
        if (form.experience_years) formData.append("experience_years", form.experience_years);
        csvToArray(form.skills).forEach((s) => formData.append("skills", s));
        csvToArray(form.services_offered).forEach((s) => formData.append("services_offered", s));
      }
      if (isStudio) {
        if (form.team_size) formData.append("team_size", form.team_size);
        formData.append("business_info", form.business_info.trim());
        csvToArray(form.services_offered).forEach((s) => formData.append("services_offered", s));
      }

      if (imageFile) {
        formData.append("profile_picture", imageFile);
      } else {
        formData.append("profile_picture", form.profile_picture.trim());
      }

      let saveRes;
      if (profile) {
        saveRes = await updateMarketplaceProfile(formData);
        showSuccessToast("Profile updated!");
      } else {
        try {
          saveRes = await createMarketplaceProfile(formData);
          showSuccessToast("Profile created!");
        } catch (createErr: any) {
          const errMessage = (
            createErr?.response?.data?.message ||
            createErr?.response?.data?.error?.message ||
            ""
          ).toLowerCase();
          if (errMessage.includes("already exist")) {
            saveRes = await updateMarketplaceProfile(formData);
            showSuccessToast("Profile created!");
          } else {
            throw createErr;
          }
        }
      }
      const savedProfile = saveRes?.data || saveRes;
      if (savedProfile) {
        localStorage.setItem("marketplace_profile", JSON.stringify(savedProfile));
      }
      window.dispatchEvent(new CustomEvent("marketplace-auth-change"));
      fetchProfile();
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? err?.response?.data?.detail ?? "Failed to save profile";
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const previewSrc = imageFile
    ? URL.createObjectURL(imageFile)
    : form.profile_picture
      ? resolveImage(form.profile_picture)
      : null;

  if (loading) {
    return (
      <PageContainer>
        <MarketplaceLoadingSpinner padding="80px 24px" />
      </PageContainer>
    );
  }

  return (
    <ProfilePage>
      <FormHero>
        <FormHeroGlow />
        <FormHeroRow>
          <FormHeroText>
            <FormHeroBadge>
              <Sparkles size={11} />
              Marketplace
            </FormHeroBadge>
            <PageTitle className="linear-text">Marketplace Profile</PageTitle>
            <FormHeroSubtitle>
              {profile
                ? "Update your public marketplace profile visible to buyers and hirers."
                : "Create your profile to start listing, posting requirements, and receiving bookings."}
            </FormHeroSubtitle>
          </FormHeroText>
          <HeroBadges>
            {profile?.is_verified && (
              <VerifiedBadge>
                <CheckCircle size={14} /> Verified
              </VerifiedBadge>
            )}
            {profile && (
              <TypeBadge $type={profile.profile_type}>
                {profile.profile_type === "studio" ? (
                  <>
                    <Building2 size={13} /> Studio
                  </>
                ) : (
                  <>
                    <Briefcase size={13} /> Freelancer
                  </>
                )}
              </TypeBadge>
            )}
          </HeroBadges>
        </FormHeroRow>
      </FormHero>

      <ProfileScroll>
        {form.is_active === false && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "14px 18px",
              marginBottom: "16px",
              borderRadius: "10px",
              background: "rgba(239, 68, 68, 0.12)",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              color: "#fca5a5",
              fontSize: "13px",
              lineHeight: "1.5",
            }}
          >
            <AlertCircle size={20} style={{ flexShrink: 0, color: "#ef4444" }} />
            <div>
              <strong>Account Frozen / Deactivated:</strong> Your marketplace account has been frozen by an administrator.
              Your profile visibility is locked as private and marketplace trading actions are suspended.
            </div>
          </div>
        )}

        {form.is_active !== false && form.is_public_locked && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              padding: "12px 16px",
              marginBottom: "16px",
              borderRadius: "10px",
              background: "rgba(245, 158, 11, 0.12)",
              border: "1px solid rgba(245, 158, 11, 0.35)",
              color: "#fcd34d",
              fontSize: "13px",
              lineHeight: "1.5",
            }}
          >
            <Lock size={18} style={{ flexShrink: 0, color: "#f59e0b" }} />
            <div>
              <strong>Profile Visibility Locked as Private:</strong> Your marketplace profile has been made private by an administrator and locked. Only a superadmin can make it public again.
            </div>
          </div>
        )}

        {!profile && (
          <InfoBanner>
            <AlertCircle size={18} />
            <span>
              You don't have a marketplace profile yet. Fill out the form below to create one and
              unlock marketplace features.
            </span>
          </InfoBanner>
        )}

        {profile && (
          <ProfileSummaryCard>
            <SummaryAvatar>
              {previewSrc ? (
                <SummaryImg src={previewSrc} alt={profile.display_name} />
              ) : (
                form.display_name.charAt(0).toUpperCase() || "?"
              )}
            </SummaryAvatar>
            <SummaryInfo>
              <SummaryName>{profile.display_name}</SummaryName>
              <SummaryMeta>
                Profile #{profile.id} ·{" "}
                {profile.profile_type === "studio" ? "Studio" : "Freelancer"} · Member since{" "}
                {new Date(profile.created_at).toLocaleDateString("en-IN", {
                  month: "short",
                  year: "numeric",
                })}
              </SummaryMeta>
            </SummaryInfo>
            <ToggleWrapper>
              <ToggleLabel style={{ display: "flex", alignItems: "center", gap: 6 }}>
                Public profile
                {form.is_public_locked && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "3px",
                      padding: "2px 6px",
                      fontSize: "11px",
                      fontWeight: 600,
                      borderRadius: "4px",
                      background: "rgba(239, 68, 68, 0.15)",
                      color: "#ef4444",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                    }}
                  >
                    <Lock size={11} /> Locked
                  </span>
                )}
              </ToggleLabel>
              <ToggleSwitch
                style={{
                  opacity: form.is_public_locked || form.is_active === false ? 0.5 : 1,
                  cursor: form.is_public_locked || form.is_active === false ? "not-allowed" : "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={form.is_public}
                  disabled={form.is_public_locked || form.is_active === false}
                  onChange={(e) => {
                    if (form.is_public_locked || form.is_active === false) return;
                    set("is_public", e.target.checked);
                  }}
                />
                <span />
              </ToggleSwitch>
            </ToggleWrapper>
          </ProfileSummaryCard>
        )}

        <form onSubmit={handleSubmit}>
          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#7c4dff">
                <User size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Basic information</SectionHeadingTitle>
                <SectionHeadingSub>Name, contact details, bio, and profile photo</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <RowGrid>
              <FormGroup>
                <FormLabel>
                  Display Name <Req>*</Req>
                </FormLabel>
                <TextInput
                  value={form.display_name}
                  onChange={(e) => set("display_name", e.target.value)}
                  placeholder="e.g. Pixel Studio"
                  required
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>
                  <PhoneIconWrap>
                    <Phone size={12} />
                  </PhoneIconWrap>
                  Contact Phone
                </FormLabel>
                <TextInput
                  value={form.contact_phone}
                  onChange={(e) => set("contact_phone", e.target.value)}
                  placeholder="e.g. 9876543210"
                />
              </FormGroup>
            </RowGrid>

            <FormGroup>
              <FormLabel>Bio / About</FormLabel>
              <TextAreaInput
                value={form.bio}
                onChange={(e) => set("bio", e.target.value)}
                rows={3}
                placeholder="Tell potential buyers and hirers about yourself or your studio…"
              />
            </FormGroup>

            <RowGrid>
              <FormGroup>
                <FormLabel>
                  <PhoneIconWrap>
                    <Mail size={12} />
                  </PhoneIconWrap>
                  Contact Email
                </FormLabel>
                <TextInput
                  type="email"
                  value={form.contact_email}
                  onChange={(e) => set("contact_email", e.target.value)}
                  placeholder="studio@example.com"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Profile Picture</FormLabel>
                <ImageUploadArea>
                  {previewSrc ? (
                    <ImagePreviewWrap>
                      <PreviewImg src={previewSrc} alt="Profile" />
                      <RemoveImgBtn
                        type="button"
                        onClick={() => {
                          setImageFile(null);
                          set("profile_picture", "");
                        }}
                      >
                        <X size={14} />
                      </RemoveImgBtn>
                      <ChangePhotoBtn type="button" onClick={() => setShowPhotoSourceModal(true)}>
                        Change photo
                      </ChangePhotoBtn>
                    </ImagePreviewWrap>
                  ) : (
                    <UploadTrigger type="button" onClick={() => setShowPhotoSourceModal(true)}>
                      <UploadIconWrap>
                        <Upload size={22} />
                      </UploadIconWrap>
                      <UploadHint>Choose profile photo</UploadHint>
                      <UploadSub>Camera or gallery</UploadSub>
                    </UploadTrigger>
                  )}
                </ImageUploadArea>
              </FormGroup>
            </RowGrid>

            {!profile && (
              <ToggleWrapper style={{ marginTop: 4 }}>
                <ToggleLabel>Make profile public</ToggleLabel>
                <ToggleSwitch>
                  <input
                    type="checkbox"
                    checked={form.is_public}
                    onChange={(e) => set("is_public", e.target.checked)}
                  />
                  <span />
                </ToggleSwitch>
              </ToggleWrapper>
            )}
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#35b7c8">
                <MapPin size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Location</SectionHeadingTitle>
                <SectionHeadingSub>Where you operate or are based</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <LocationGrid>
              <FormGroup>
                <FormLabel>City</FormLabel>
                <TextInput
                  value={form.city}
                  onChange={(e) => set("city", e.target.value)}
                  placeholder="Mumbai"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>State</FormLabel>
                <TextInput
                  value={form.state}
                  onChange={(e) => set("state", e.target.value)}
                  placeholder="Maharashtra"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Country</FormLabel>
                <TextInput
                  value={form.country}
                  onChange={(e) => set("country", e.target.value)}
                  placeholder="India"
                />
              </FormGroup>
            </LocationGrid>
          </FormCard>

          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#c94daa">
                <Tags size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Work categories</SectionHeadingTitle>
                <SectionHeadingSub>Help clients discover your profile</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            <FormGroup>
              <FormLabel>Categories (comma-separated)</FormLabel>
              <TextInput
                value={form.work_categories}
                onChange={(e) => set("work_categories", e.target.value)}
                placeholder="e.g. wedding, corporate, portrait"
              />
              <HintText>Separate multiple categories with commas.</HintText>
            </FormGroup>
          </FormCard>

          {isFreelancer && (
            <FormCard>
              <SectionHeading>
                <SectionIcon $tone="#7c4dff">
                  <Briefcase size={20} />
                </SectionIcon>
                <SectionHeadingBody>
                  <SectionHeadingTitle>Professional details</SectionHeadingTitle>
                  <SectionHeadingSub>Your skills, experience, and services</SectionHeadingSub>
                </SectionHeadingBody>
              </SectionHeading>

              <RowGrid>
                <FormGroup>
                  <FormLabel>Professional Type</FormLabel>
                  <SelectDropdown
                    value={form.professional_type}
                    onChange={(e) =>
                      set("professional_type", e.target.value as ProfessionalType | "")
                    }
                  >
                    <option value="">Select type</option>
                    <option value="photographer">Photographer</option>
                    <option value="videographer">Videographer</option>
                    <option value="both">Both</option>
                  </SelectDropdown>
                </FormGroup>
                <FormGroup>
                  <FormLabel>Profession / Title</FormLabel>
                  <TextInput
                    value={form.profession}
                    onChange={(e) => set("profession", e.target.value)}
                    placeholder="e.g. Wedding Photographer"
                  />
                </FormGroup>
              </RowGrid>

              <RowGrid>
                <FormGroup>
                  <FormLabel>Years of Experience</FormLabel>
                  <TextInput
                    type="number"
                    min="0"
                    value={form.experience_years}
                    onChange={(e) => set("experience_years", e.target.value)}
                    placeholder="e.g. 8"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Skills (comma-separated)</FormLabel>
                  <TextInput
                    value={form.skills}
                    onChange={(e) => set("skills", e.target.value)}
                    placeholder="e.g. candid, drone, editing"
                  />
                </FormGroup>
              </RowGrid>

              <FormGroup>
                <FormLabel>Services Offered (comma-separated)</FormLabel>
                <TextInput
                  value={form.services_offered}
                  onChange={(e) => set("services_offered", e.target.value)}
                  placeholder="e.g. photography, pre-wedding, portraits"
                />
              </FormGroup>
            </FormCard>
          )}

          {isStudio && (
            <FormCard>
              <SectionHeading>
                <SectionIcon $tone="#f09a3e">
                  <Building2 size={20} />
                </SectionIcon>
                <SectionHeadingBody>
                  <SectionHeadingTitle>Studio details</SectionHeadingTitle>
                  <SectionHeadingSub>Team size, services, and business info</SectionHeadingSub>
                </SectionHeadingBody>
              </SectionHeading>

              <RowGrid>
                <FormGroup>
                  <FormLabel>Team Size</FormLabel>
                  <TextInput
                    type="number"
                    min="1"
                    value={form.team_size}
                    onChange={(e) => set("team_size", e.target.value)}
                    placeholder="e.g. 12"
                  />
                </FormGroup>
                <FormGroup>
                  <FormLabel>Services Offered (comma-separated)</FormLabel>
                  <TextInput
                    value={form.services_offered}
                    onChange={(e) => set("services_offered", e.target.value)}
                    placeholder="e.g. wedding, corporate, rental"
                  />
                </FormGroup>
              </RowGrid>

              <FormGroup>
                <FormLabel>Business Info</FormLabel>
                <TextAreaInput
                  value={form.business_info}
                  onChange={(e) => set("business_info", e.target.value)}
                  rows={2}
                  placeholder="e.g. GST registered, established 2018"
                />
              </FormGroup>
            </FormCard>
          )}

          <FormCard>
            <SectionHeading>
              <SectionIcon $tone="#31a66a">
                <Layers size={20} />
              </SectionIcon>
              <SectionHeadingBody>
                <SectionHeadingTitle>Portfolio</SectionHeadingTitle>
                <SectionHeadingSub>Showcase your best work on your public profile</SectionHeadingSub>
              </SectionHeadingBody>
            </SectionHeading>

            {form.portfolio.length > 0 && (
              <PortfolioList>
                {form.portfolio.map((item, i) => (
                  <PortfolioItemRow key={i}>
                    <PortfolioThumb>
                      {item.url ? (
                        <img
                          src={item.url}
                          alt={item.title}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = "none";
                          }}
                        />
                      ) : (
                        <ImageIcon size={18} color="rgba(23,23,23,0.3)" />
                      )}
                    </PortfolioThumb>
                    <PortfolioTitle>{item.title}</PortfolioTitle>
                    <RemoveBtn type="button" onClick={() => removePortfolioItem(i)}>
                      <X size={14} />
                    </RemoveBtn>
                  </PortfolioItemRow>
                ))}
              </PortfolioList>
            )}

            <PortfolioAddRow>
              <TextInput
                value={newPortTitle}
                onChange={(e) => setNewPortTitle(e.target.value)}
                placeholder="Title, e.g. Beach Wedding"
                style={{ flex: 1 }}
              />
              <TextInput
                value={newPortUrl}
                onChange={(e) => setNewPortUrl(e.target.value)}
                placeholder="Image URL"
                style={{ flex: 2 }}
              />
              <AddPortBtn type="button" onClick={addPortfolioItem}>
                <Plus size={15} /> Add
              </AddPortBtn>
            </PortfolioAddRow>
            <HintText>Add links to your best work. These appear on your public profile.</HintText>
          </FormCard>

          <EndFooter>
            <SubmitHint>
              Profile visibility:{" "}
              <strong>
                {form.is_public_locked
                  ? "Private (Locked by Admin)"
                  : form.is_public
                  ? "Public"
                  : "Private"}
              </strong>
              {form.is_public_locked
                ? " — locked by administrator and cannot be republished."
                : form.is_public
                ? " — visible to everyone on the marketplace."
                : " — hidden from public browse."}
            </SubmitHint>
            <SaveButton type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size={16} /> Saving…
                </>
              ) : (
                <>
                  <Save size={16} />
                  {profile ? "Update Profile" : "Create Profile"}
                </>
              )}
            </SaveButton>
          </EndFooter>
        </form>
      </ProfileScroll>

      {showPhotoSourceModal &&
        createPortal(
          <ModalOverlay onClick={() => setShowPhotoSourceModal(false)}>
            <PhotoModalShell onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <HeroGlow />
                <HeroRow>
                  <HeroText>
                    <HeroBadge>
                      <Camera size={11} />
                      Photo
                    </HeroBadge>
                    <ModalTitle>Select Profile Photo</ModalTitle>
                    <ModalSubtitle>Take a new photo or choose one from your gallery.</ModalSubtitle>
                  </HeroText>
                  <CloseBtn type="button" onClick={() => setShowPhotoSourceModal(false)} aria-label="Close">
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>
              <ModalBody>
                <OptionGrid>
                  <OptionCard type="button" onClick={startCamera}>
                    <OptionIcon $tone="#c94daa">
                      <Camera size={22} />
                    </OptionIcon>
                    <OptionLabel>Take Photo</OptionLabel>
                    <OptionSub>Use your device camera</OptionSub>
                  </OptionCard>
                  <OptionCard
                    type="button"
                    onClick={() => document.getElementById("gallery-image-upload")?.click()}
                  >
                    <OptionIcon $tone="#ffc951">
                      <ImageIcon size={22} />
                    </OptionIcon>
                    <OptionLabel>Choose from Gallery</OptionLabel>
                    <OptionSub>Pick an existing image</OptionSub>
                  </OptionCard>
                </OptionGrid>
                <input
                  id="gallery-image-upload"
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
              </ModalBody>
            </PhotoModalShell>
          </ModalOverlay>,
          document.body
        )}

      {showCamera &&
        createPortal(
          <ModalOverlay>
            <PhotoModalShell onClick={(e) => e.stopPropagation()}>
              <ModalHero>
                <HeroGlow />
                <HeroRow>
                  <HeroText>
                    <HeroBadge>
                      <Camera size={11} />
                      Camera
                    </HeroBadge>
                    <ModalTitle>Camera Preview</ModalTitle>
                    <ModalSubtitle>Position yourself and capture your profile photo.</ModalSubtitle>
                  </HeroText>
                  <CloseBtn type="button" onClick={stopCamera} aria-label="Close">
                    <X size={18} />
                  </CloseBtn>
                </HeroRow>
              </ModalHero>
              <ModalBody>
                <CameraContainer>
                  <CameraVideo
                    id="webcam-video"
                    autoPlay
                    playsInline
                    ref={(el) => {
                      if (el && cameraStream) el.srcObject = cameraStream;
                    }}
                  />
                </CameraContainer>
              </ModalBody>
              <ModalActions style={{ flexDirection: "row", gap: 10 }}>
                <SecondaryButton type="button" onClick={stopCamera} style={{ flex: 1 }}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="button" onClick={capturePhoto} style={{ flex: 1.4 }}>
                  Capture Photo
                </PrimaryButton>
              </ModalActions>
            </PhotoModalShell>
          </ModalOverlay>,
          document.body
        )}
    </ProfilePage>
  );
};

export default MarketplaceProfilePage;

const HeroBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const VerifiedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  background: rgba(22, 163, 74, 0.1);
  color: #16a34a;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid rgba(22, 163, 74, 0.2);
`;

const TypeBadge = styled.div<{ $type: ProfileType }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 100px;
  background: ${(p) =>
    p.$type === "freelancer" ? "rgba(124, 77, 255, 0.1)" : "rgba(201, 77, 170, 0.1)"};
  color: ${(p) => (p.$type === "freelancer" ? "#7c4dff" : "#c94daa")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid
    ${(p) => (p.$type === "freelancer" ? "rgba(124, 77, 255, 0.2)" : "rgba(201, 77, 170, 0.2)")};
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(201, 77, 170, 0.06) 0%, rgba(255, 201, 81, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.16);
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  line-height: 1.5;
  flex-shrink: 0;
`;

const ProfileSummaryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 18px 20px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid rgba(23, 23, 23, 0.06);
  box-shadow: 0 8px 24px rgba(23, 23, 23, 0.05);
  flex-wrap: wrap;
  flex-shrink: 0;
`;

const SummaryAvatar = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: Urbanist, sans-serif;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  background: ${gradient};
  box-shadow: 0 4px 14px rgba(201, 77, 170, 0.25);
`;

const SummaryImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SummaryInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const SummaryName = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #171717;
  letter-spacing: -0.01em;
  margin-bottom: 4px;
`;

const SummaryMeta = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const PhoneIconWrap = styled.span`
  display: inline-flex;
  margin-right: 4px;
  vertical-align: middle;
`;

const LocationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const HintText = styled.p`
  margin: 4px 0 0;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.42);
`;

const ImageUploadArea = styled.div`
  border: 2px dashed rgba(23, 23, 23, 0.1);
  border-radius: 14px;
  overflow: hidden;
  min-height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
  }
`;

const UploadTrigger = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  min-height: 130px;
  cursor: pointer;
  padding: 16px;
  border: none;
  background: none;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.03);
  }
`;

const UploadIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c94daa;
  background: rgba(201, 77, 170, 0.1);
`;

const UploadHint = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgba(23, 23, 23, 0.62);
`;

const UploadSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.38);
`;

const ImagePreviewWrap = styled.div`
  position: relative;
  width: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const PreviewImg = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 0 6px 20px rgba(201, 77, 170, 0.2);
  border: 3px solid #fff;
`;

const RemoveImgBtn = styled.button`
  position: absolute;
  top: 18px;
  right: calc(50% - 66px);
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #ef4444;
    color: white;
  }
`;

const ChangePhotoBtn = styled.button`
  padding: 6px 14px;
  border-radius: 100px;
  border: 1px solid rgba(201, 77, 170, 0.25);
  background: rgba(201, 77, 170, 0.06);
  color: #c94daa;
  font-family: Urbanist, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.12);
  }
`;

const PortfolioList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 4px;
`;

const PortfolioItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: 1px solid rgba(23, 23, 23, 0.07);
  border-radius: 12px;
  background: #fafafa;
`;

const PortfolioThumb = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(23, 23, 23, 0.06);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PortfolioTitle = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: #171717;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const RemoveBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 9px;
  border: none;
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: #ef4444;
    color: white;
  }
`;

const PortfolioAddRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const AddPortBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 48px;
  padding: 0 18px;
  border: none;
  border-radius: 12px;
  background: rgba(201, 77, 170, 0.1);
  color: #c94daa;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.18);
    transform: translateY(-1px);
  }
`;

const PhotoModalShell = styled(ModalShell)`
  max-width: 440px;
`;

const OptionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 16px;
  border-radius: 16px;
  border: 1px solid rgba(23, 23, 23, 0.08);
  background: #fafafa;
  cursor: pointer;
  text-align: center;
  transition: all 0.15s ease;

  &:hover {
    border-color: rgba(201, 77, 170, 0.28);
    background: rgba(201, 77, 170, 0.04);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(201, 77, 170, 0.1);
  }
`;

const OptionIcon = styled.div<{ $tone: string }>`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(p) => p.$tone};
  background: ${(p) => `${p.$tone}18`};
`;

const OptionLabel = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: #171717;
`;

const OptionSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.48);
`;

const CameraContainer = styled.div`
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #000;
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
